import { useCallback, useEffect, useMemo, useRef } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import type {
  TextMessageResponseDto,
  CreateQuoteResponseVersionDto,
  QuoteMessageResponseDto,
  QuoteMessageResponseDtoQuoteDataItemsItem,
  ContractMessageResponseDto,
  SystemMessageResponseDto,
  PaymentPendingMessageResponseDto,
  TextMessageResponseDtoSenderType,
} from "@semo/api-contracts";
import {
  useChatControllerSendMessage,
  useChatControllerUploadFile,
  useChatControllerSubmitQuoteRevision,
  useContractsControllerCreateContract,
  useContractsControllerRequestCancellation,
  getContractsControllerGetMyContractsQueryKey,
  ApiError,
} from "@semo/api-contracts";
import type { CreateContractRequestDto } from "@semo/api-contracts";
import type { PendingChatMessage, PendingQuoteMessage } from "../types/message";
import { generateTempId } from "../types/message";
import { useRealtimeChat } from "./useRealtimeChat";
import { useChatCache, type CacheMessage } from "./useChatCache";
import { useReadReceipt } from "./useReadReceipt";
import {
  chatMessagesQueryOptions,
  chatMessagesRefetchOnMount,
} from "./chatMessagesQuery";
import {
  FILE_ATTACH_FAILED_MESSAGE,
  clientErrorMessage,
} from "../utils/chat-error-messages";

/**
 * Options for useChatMessages hook
 */
/**
 * 업로드 실패가 «파일 자체가 거절된» 경우인가 — 다시 보내도 같은 결과인 4xx.
 * 요청 시간 초과(408)·요청 과다(429)는 다시 보내면 될 수 있으므로 제외한다.
 */
function isFileRejected(error: ApiError): boolean {
  return (
    error.isClientError && error.statusCode !== 408 && error.statusCode !== 429
  );
}

export interface UseChatMessagesOptions {
  roomId: string;
  currentUserId: string;
  currentUserType: TextMessageResponseDtoSenderType;
  supabaseClient: SupabaseClient;
  /** Toast notification callback */
  onToast?: (message: string, type: "success" | "error" | "info") => void;
  /** Whether to enable message fetching. Defaults to true. */
  enabled?: boolean;
}

/**
 * Return type for useChatMessages hook
 */
export interface UseChatMessagesReturn {
  messages: (PendingChatMessage | PendingQuoteMessage)[];
  isLoading: boolean;
  isSending: boolean;
  isUploading: boolean;
  isSubmittingQuote: boolean;
  isCreatingContract: boolean;
  sendMessage: (content: string) => void;
  uploadFile: (file: File) => void;
  submitQuoteRevision: (quoteData: CreateQuoteResponseVersionDto) => void;
  createContract: (
    contractRequest: CreateContractRequestDto,
    displayFields: { itemName?: string; merchantName?: string },
  ) => void;
  requestContractCancellation: (params: {
    contractId: string;
    reason: string;
    refundBank?: string;
    refundAccountNumber?: string;
    refundAccountHolder?: string;
  }) => void;
  markMessagesAsRead: (
    messagesToMark?: (
      | PendingChatMessage
      | PendingQuoteMessage
      | TextMessageResponseDto
      | QuoteMessageResponseDto
    )[],
  ) => void;
  injectManualMessage: (
    message:
      | TextMessageResponseDto
      | QuoteMessageResponseDto
      | ContractMessageResponseDto
      | SystemMessageResponseDto
      | PaymentPendingMessageResponseDto,
  ) => void;
  resendMessage: (messageId: string) => void;
  hasMoreMessages: boolean;
  isFetchingPrevious: boolean;
  fetchPreviousMessages: () => Promise<void>;
  refetch: () => Promise<any>;
  isFetching: boolean;
  channel?: any;
}

/**
 * Comprehensive chat messages management hook
 * Uses Facade Pattern to orchestrate Queries, Mutations, Cache, and Realtime
 */
export function useChatMessages(
  options: UseChatMessagesOptions,
): UseChatMessagesReturn {
  const {
    roomId,
    currentUserId,
    currentUserType,
    supabaseClient,
    onToast,
    enabled = true,
  } = options;

  const onToastRef = useRef(onToast);
  // Cache Layer (Repository)
  const cache = useChatCache(roomId);

  useEffect(() => {
    onToastRef.current = onToast;
  }, [onToast]);

  // 1. Realtime & Handlers (Moved up for Connection State access)
  const handleNewMessage = useCallback(
    (newMsg: any) => {
      if (newMsg.messageType === "contract") {
        onToastRef.current?.("계약이 체결되었습니다.", "success");
      }
      // 서버 응답이 모르는 메시지 → 진행 중인 GET 이 덮어쓰지 못하게 끊고 넣는다.
      cache.upsertMessage(newMsg, undefined, { cancelInFlightFetch: true });
    },
    [cache],
  );

  const handleMessageRead = useCallback(
    (data: { lastReadMessageId: string; userId: string }) => {
      // Only update if the OTHER party read the message.
      // If I read, it doesn't change the 'read by other' status of my sent messages.
      if (data.userId !== currentUserId) {
        cache.markAsReadOptimistic(data.lastReadMessageId, currentUserId);
      }
    },
    [cache, currentUserId],
  );

  const handleMessageUpdated = useCallback(
    (data: { id: string; [key: string]: unknown }) => {
      cache.upsertMessage(data as unknown as CacheMessage, undefined, {
        cancelInFlightFetch: true,
      });
      if (data.metadata && (data.metadata as any).status === "APPROVED") {
        onToastRef.current?.("결제가 승인되었습니다.", "success");
      }
    },
    [cache],
  );

  // Capture connection state to control polling fallback
  const { isConnected, channel } = useRealtimeChat({
    roomId,
    supabaseClient,
    userId: currentUserId,
    onNewMessage: handleNewMessage,
    onMessageRead: handleMessageRead,
    onMessageUpdated: handleMessageUpdated,
  });

  // 2. Query (Fetch) - with Smart Polling Fallback
  //    키·조회 함수는 `prefetchChatMessages` 와 같은 설정을 쓴다 — 발주기관 채팅창이 방 정보와
  //    함께 미리 불러 둔 결과를 그대로 이어받는다.
  const messagesQueryOptions = useMemo(
    () => chatMessagesQueryOptions(roomId),
    [roomId],
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    refetch,
  } = useInfiniteQuery({
    ...messagesQueryOptions,
    enabled, // ✅ Controlled via options
    // 붙을 때마다 새로 부른다 — 단, 방금(2초 안) 받아 둔 목록이면 그대로 쓴다(미리 부르기와 중복 방지).
    refetchOnMount: chatMessagesRefetchOnMount,
    refetchOnWindowFocus: true, // Re-sync on focus
    // ✅ Smart Fallback: Poll every 3s only if Realtime is disconnected AND enabled
    refetchInterval: isConnected || !enabled ? false : 3000,
  });

  // ✅ Gap Fill: Trigger immediate refetch when socket reconnects
  const wasConnected = useRef(isConnected);
  useEffect(() => {
    if (!wasConnected.current && isConnected) {
      refetch();
    }
    wasConnected.current = isConnected;
  }, [isConnected, refetch]);

  // 3. View Model (Derived State)
  const messages = useMemo(() => {
    if (!data?.pages) return [];
    // API returns pages in DESC order (Page 0: Newest, Page N: Oldest)
    // Items in each page are also DESC.
    // We want final result to be ASC (Oldest -> Newest) for the UI
    const allItems = data.pages.flatMap((page) => page.data?.items || []);

    // Explicitly sort by createdAt ASC to guarantee order regardless of API/Cache state
    return [...allItems].sort((a, b) => {
      return a.createdAt > b.createdAt ? 1 : -1;
    }) as (PendingChatMessage | PendingQuoteMessage)[];
  }, [data?.pages]);

  // 4. Mutations
  const { mutate: sendMessageMutation, isPending: isSending } =
    useChatControllerSendMessage();
  const { mutate: uploadFileMutation, isPending: isUploading } =
    useChatControllerUploadFile();
  const { mutate: submitQuoteRevisionMutation, isPending: isSubmittingQuote } =
    useChatControllerSubmitQuoteRevision();
  // useChatControllerMarkAsRead is removed from here as it is used in useReadReceipt
  const queryClient = useQueryClient();
  const { mutate: createContractMutation, isPending: isCreatingContract } =
    useContractsControllerCreateContract();
  const { mutate: requestCancellationMutation } =
    useContractsControllerRequestCancellation();

  // 5. Read Receipt Logic (Decoupled)
  const { markMessagesAsRead } = useReadReceipt({
    roomId,
    currentUserId,
    messages,
  });

  // 6. Action Handlers (Business Logic)
  const sendMessage = useCallback(
    (content: string) => {
      const tempId = generateTempId();
      const tempMessage: PendingChatMessage = {
        id: tempId,
        tempId,
        roomId,
        message: content,
        senderId: currentUserId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messageType: "text",
        isPending: true,
        isRead: false,
        isDeleted: false,
        isSystemMessage: false,
        senderType: currentUserType,
        isError: false,
        retryPayload: content, // For text, payload is just string
      };

      cache.upsertMessage(tempMessage);

      sendMessageMutation(
        { roomId, data: { content, tempId } as any },
        {
          onSuccess: (response: any) => {
            cache.upsertMessage(response.data, tempId);
          },
          onError: (error: any) => {
            const e = ApiError.fromUnknown(error);
            console.error(e);
            cache.upsertMessage({
              ...tempMessage,
              isPending: false,
              isError: true,
            });
            onToastRef.current?.(
              e.userMessage || "메시지 전송에 실패했습니다.",
              "error",
            );
          },
        },
      );
    },
    [roomId, currentUserId, currentUserType, sendMessageMutation, cache],
  );

  const uploadFile = useCallback(
    (file: File) => {
      const tempId = generateTempId();
      const blobUrl = URL.createObjectURL(file);
      const isImage = file.type.startsWith("image/");

      const tempMessage: PendingChatMessage = {
        id: tempId,
        tempId,
        roomId,
        message: "",
        senderId: currentUserId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messageType: "attachment",
        isPending: true,
        isRead: false,
        isDeleted: false,
        isSystemMessage: false,
        senderType: currentUserType,
        isError: false,
        retryPayload: { file }, // Store File object
        attachment: {
          id: tempId,
          url: blobUrl,
          fileName: file.name,
          fileSize: file.size,
          isImage: isImage,
          contentType: file.type,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as any,
      };
      cache.upsertMessage(tempMessage);

      uploadFileMutation(
        { roomId, data: { file, tempId } as any },
        {
          onSuccess: (response: any) => {
            cache.upsertMessage(response.data, tempId);
            URL.revokeObjectURL(blobUrl);
          },
          onError: (error: any) => {
            const e = ApiError.fromUnknown(error);
            if (isFileRejected(e)) {
              /**
               * 서버가 파일 자체를 거절했다(형식·용량 등 4xx) — 다시 보내도 같은 결과라
               * «전송 실패 [재전송]» 말풍선을 남기지 않고 지운 뒤 사유만 알린다(GS A3-27).
               * 네트워크·서버 오류(5xx)·요청 과다(429)는 종전대로 재전송을 남긴다.
               */
              cache.removeMessage(tempId, tempId);
              URL.revokeObjectURL(blobUrl);
            } else {
              cache.upsertMessage({
                ...tempMessage,
                isPending: false,
                isError: true,
              });
            }
            // Note: We don't revoke URL here so retry can display preview?
            // Actually blob URLs might persist.
            // 서버가 준 4xx 사유(형식·용량 등)는 그대로, 그 밖(서버 오류·연결 끊김)은 매뉴얼 R06
            // 5.2.4 14번 문구로 알린다.
            onToastRef.current?.(
              clientErrorMessage(e, FILE_ATTACH_FAILED_MESSAGE),
              "error",
            );
          },
        },
      );
    },
    [roomId, currentUserId, currentUserType, uploadFileMutation, cache],
  );

  // Quote Revision
  const submitQuoteRevision = useCallback(
    (quoteData: CreateQuoteResponseVersionDto) => {
      const tempId = generateTempId();
      const tempMessage: PendingQuoteMessage = {
        id: tempId,
        tempId,
        roomId,
        senderId: currentUserId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messageType: "quote",
        message: quoteData.description || "견적서를 전송하였습니다.",
        isPending: true,
        isRead: false,
        isDeleted: false,
        isSystemMessage: false,
        senderType: "supplier",
        isError: false,
        retryPayload: quoteData,
        quoteData: {
          totalAmount: quoteData.totalAmount ?? 0,
          items: (quoteData.items?.flat().filter(Boolean) ||
            []) as QuoteMessageResponseDtoQuoteDataItemsItem[],
          description: quoteData.description,
          versionNumber: (quoteData as any).versionNumber || 1,
          createdAt: new Date().toISOString(),
        } as any,
      };
      cache.upsertMessage(tempMessage);

      submitQuoteRevisionMutation(
        { roomId, data: { ...quoteData, tempId } as any },
        {
          onSuccess: (response: any) => {
            cache.upsertMessage(response.data, tempId);
          },
          onError: (error: any) => {
            const e = ApiError.fromUnknown(error);
            console.error(e);
            cache.upsertMessage({
              ...tempMessage,
              isPending: false,
              isError: true,
            });
            onToastRef.current?.(
              e.userMessage || "견적 발송에 실패했습니다.",
              "error",
            );
          },
        },
      );
    },
    [roomId, currentUserId, submitQuoteRevisionMutation, cache],
  );

  // Create Contract
  const createContract = useCallback(
    (contractRequest: CreateContractRequestDto, displayFields: any) => {
      const tempId = generateTempId();
      const tempMessage: PendingChatMessage = {
        id: tempId,
        tempId,
        roomId,
        senderId: currentUserId,
        message: "[계약 진행 중...]",
        messageType: "contract",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isPending: true,
        isRead: false,
        isDeleted: false,
        isSystemMessage: false,
        senderType: currentUserType,
        isError: false,
        retryPayload: { request: contractRequest, display: displayFields },
        contractInfo: {
          contractId: "",
          status: "pending",
          totalAmount: contractRequest.totalAmount,
          contractedAt: new Date().toISOString(),
          itemName: displayFields.itemName || "견적 항목",
          merchantName: displayFields.merchantName || "",
        } as any,
      };
      cache.upsertMessage(tempMessage);

      createContractMutation(
        { data: contractRequest },
        {
          onSuccess: (response: any) => {
            cache.upsertMessage(response.data, tempId);

            // 채팅에서 체결해도 «나의 계약» 목록은 갱신돼야 한다. 그 목록은 탭마다
            // 캐시가 갈린다(전체·완료·취소됨이 각각 { statuses } 를 키에 단다).
            // 인자 없이 만든 ["/contracts/me"] 를 접두사로 넘겨 세 탭을 한 번에
            // 무효화한다. 한 탭만 무효화하면 이미 캐시된 다른 탭이 staleTime(5분)
            // 동안 새 계약을 빠뜨린 목록을 계속 보여준다.
            void queryClient.invalidateQueries({
              queryKey: getContractsControllerGetMyContractsQueryKey(),
            });
          },
          onError: (error: any) => {
            const e = ApiError.fromUnknown(error);
            console.error(e);
            cache.upsertMessage({
              ...tempMessage,
              isPending: false,
              isError: true,
            });
            onToastRef.current?.(
              e.userMessage || "계약 요청에 실패했습니다.",
              "error",
            );
          },
        },
      );
    },
    [roomId, currentUserId, currentUserType, createContractMutation, cache, queryClient],
  );

  // Request Contract Cancellation
  const requestContractCancellation = useCallback(
    (params: {
      contractId: string;
      reason: string;
      refundBank?: string;
      refundAccountNumber?: string;
      refundAccountHolder?: string;
    }) => {
      const tempId = generateTempId();
      const tempMessage: PendingChatMessage = {
        id: tempId,
        tempId,
        roomId,
        senderId: currentUserId,
        message: "구매자가 계약 취소를 요청했습니다.",
        messageType: "cancellation_request",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isPending: true,
        isRead: false,
        isDeleted: false,
        isSystemMessage: false,
        senderType: currentUserType,
        isError: false,
        retryPayload: params,
        cancellationRequestData: {
          refundId: "",
          contractId: params.contractId,
          requestReason: params.reason,
          status: "pending",
        } as any,
      };
      cache.upsertMessage(tempMessage);

      requestCancellationMutation(
        {
          id: params.contractId,
          data: {
            refund_reason: params.reason,
            refund_bank: params.refundBank,
            refund_account_number: params.refundAccountNumber,
            refund_account_holder: params.refundAccountHolder,
            tempId, // ✅ Support Optimistic UI
          },
        },
        {
          onSuccess: (response: any) => {
            // Response might be empty or notification of success
            // Realtime event will usually replace the temp message
            // But we can update if server returns the new message
            if (response?.data) {
              cache.upsertMessage(response.data, tempId);
            }
          },
          onError: (error: any) => {
            const e = ApiError.fromUnknown(error);
            console.error(e);
            cache.upsertMessage({
              ...tempMessage,
              isPending: false,
              isError: true,
            });
            onToastRef.current?.(
              e.userMessage || "취소 요청에 실패했습니다.",
              "error",
            );
          },
        },
      );
    },
    [
      roomId,
      currentUserId,
      currentUserType,
      requestCancellationMutation,
      cache,
    ],
  );

  // Retry logic for failed messages (Moved below dependencies to avoid TDZ)
  const resendMessage = useCallback(
    (messageId: string) => {
      const failedMsg = messages.find((m) => m.id === messageId);
      if (!failedMsg || !failedMsg.isError) return;

      // Remove the old failed message first
      cache.removeMessage(messageId, failedMsg.tempId);

      switch (failedMsg.messageType) {
        case "text":
          sendMessage(failedMsg.message);
          break;
        case "attachment":
          if (failedMsg.retryPayload?.file) {
            uploadFile(failedMsg.retryPayload.file);
          }
          break;
        case "quote":
          if (failedMsg.retryPayload) {
            submitQuoteRevision(failedMsg.retryPayload);
          }
          break;
        case "contract":
          if (failedMsg.retryPayload) {
            createContract(
              failedMsg.retryPayload.request,
              failedMsg.retryPayload.display,
            );
          }
          break;
      }
    },
    [
      messages,
      cache,
      sendMessage,
      uploadFile,
      submitQuoteRevision,
      createContract,
    ],
  );

  return {
    messages,
    isLoading,
    isSending,
    isUploading,
    isSubmittingQuote,
    isCreatingContract,
    sendMessage,
    uploadFile,
    submitQuoteRevision,
    createContract,
    requestContractCancellation,
    markMessagesAsRead,
    injectManualMessage: (data) =>
      cache.upsertMessage(data as unknown as CacheMessage),
    resendMessage,
    hasMoreMessages: !!hasNextPage,
    isFetchingPrevious: isFetchingNextPage,
    fetchPreviousMessages: useCallback(async () => {
      await fetchNextPage();
    }, [fetchNextPage]),
    refetch: async () => {
      await refetch();
    },
    isFetching,
    channel,
  };
}
